import { PlatformUser } from '@provision/entity-types';
import { useCallback, useMemo } from 'react';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import {
  useBlockedEmail,
  useCreateBlockedEmail,
  useDeleteBlockedEmail,
} from 'src/graphql/entities/blocked-email';
import {
  useBlockedPhoneNumber,
  useCreateBlockedPhoneNumber,
  useDeleteBlockedPhoneNumber,
} from 'src/graphql/entities/blocked-phone-number';
import { DEFAULT_PAGINATION_LIMIT } from 'src/graphql/entities/search/use-search';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

export type BlockedContactType = 'email' | 'phone';

export type BlockedContact = {
  id: string;
  type: BlockedContactType;
  value: string;
  dateAdded: string;
  addedBy: PlatformUser;
  partnerId: number;
};

export type BlockedContactTypeFilter = BlockedContact['type'] | 'all';

// useBlockedContacts incorporates two entities: BlockedEmail and BlockedPhoneNumber to represent
// them as unified BlockedContact entity on UI.
export const useBlockedContacts = (
  type: BlockedContactTypeFilter = 'all',
  searchTerm = '',
) => {
  const delayedFilter = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_DELAY);

  const blockedEmailsFilters = useMemo(() => {
    if (!delayedFilter) return [];
    return [
      {
        attribute: 'blockedEmail',
        operator: 'ilike',
        value: `%${delayedFilter}%`,
      },
    ];
  }, [delayedFilter]);

  const {
    blockedEmails,
    isLoading: isLoadingBlockedEmails,
    loadMore: loadMoreBlockedEmails,
    hasMore: hasMoreBlockedEmails,
  } = useBlockedEmail(
    {
      filters: blockedEmailsFilters,
      sorting: [{ attribute: 'updatedAt', desc: true }],
      limit: DEFAULT_PAGINATION_LIMIT,
    },
    { skip: type === 'phone' },
  );
  const { createBlockedEmail, isCreating: isCreatingBlockedEmail } = useCreateBlockedEmail();
  const { deleteBlockedEmailsById, isDeleting: isDeletingBlockedEmail } =
    useDeleteBlockedEmail();

  const blockedPhoneNumberFilters = useMemo(() => {
    if (!delayedFilter) return [];

    return [
      {
        attribute: 'blockedPhoneNumber',
        operator: 'ilike',
        value: `%${delayedFilter}%`,
      },
    ];
  }, [delayedFilter]);

  const {
    blockedPhoneNumbers,
    isLoading: isLoadingBlockedPhoneNumbers,
    loadMore: loadMoreBlockedPhoneNumbers,
    hasMore: hasMoreBlockedPhoneNumbers,
  } = useBlockedPhoneNumber(
    {
      filters: blockedPhoneNumberFilters,
      sorting: [{ attribute: 'updatedAt', desc: true }],
      limit: DEFAULT_PAGINATION_LIMIT,
    },
    { skip: type === 'email' },
  );

  const { createBlockedPhoneNumber, isCreating: isCreatingBlockedPhoneNumber } =
    useCreateBlockedPhoneNumber();
  const { deleteBlockedPhoneNumbersById, isDeleting: isDeletingBlockedPhoneNumber } =
    useDeleteBlockedPhoneNumber();

  const loadMore = useCallback(async (): Promise<void> => {
    const loadMoreRequests: Promise<void>[] = [];
    if (hasMoreBlockedEmails) loadMoreRequests.push(loadMoreBlockedEmails());
    if (hasMoreBlockedPhoneNumbers) loadMoreRequests.push(loadMoreBlockedPhoneNumbers());
    await Promise.all(loadMoreRequests);
  }, [
    hasMoreBlockedEmails,
    hasMoreBlockedPhoneNumbers,
    loadMoreBlockedEmails,
    loadMoreBlockedPhoneNumbers,
  ]);

  const createBlockedContact = useCallback(
    async (type: BlockedContactType, value: BlockedContact['value']) => {
      switch (type) {
        case 'email':
          await createBlockedEmail(value);
          break;
        case 'phone':
          await createBlockedPhoneNumber(value);
          break;
      }
    },
    [createBlockedEmail, createBlockedPhoneNumber],
  );

  const deleteBlockedContact = useCallback(
    async (type: BlockedContactType, id: string) => {
      switch (type) {
        case 'email':
          await deleteBlockedEmailsById([id]);
          break;
        case 'phone':
          await deleteBlockedPhoneNumbersById([id]);
          break;
      }
    },
    [deleteBlockedEmailsById, deleteBlockedPhoneNumbersById],
  );

  const blockedContacts: BlockedContact[] = useMemo(() => {
    const contacts: BlockedContact[] = [];
    if (type !== 'phone') {
      const emailContacts = blockedEmails.map<BlockedContact>(
        ({ id, blockedEmail, createdByPlatformUser, createdAt, partnerId }) => ({
          id,
          value: blockedEmail,
          type: 'email',
          addedBy: createdByPlatformUser,
          dateAdded: createdAt,
          partnerId,
        }),
      );
      contacts.push(...emailContacts);
    }
    if (type !== 'email') {
      const phoneContacts = blockedPhoneNumbers.map<BlockedContact>(
        ({ id, blockedPhoneNumber, createdByPlatformUser, createdAt, partnerId }) => ({
          id,
          value: blockedPhoneNumber,
          type: 'phone',
          addedBy: createdByPlatformUser,
          dateAdded: createdAt,
          partnerId,
        }),
      );
      contacts.push(...phoneContacts);
    }
    return contacts;
  }, [blockedEmails, blockedPhoneNumbers, type]);

  return {
    blockedContacts,
    createBlockedContact,
    deleteBlockedContact,
    isLoading: isLoadingBlockedEmails || isLoadingBlockedPhoneNumbers,
    isSaving:
      isCreatingBlockedEmail ||
      isCreatingBlockedPhoneNumber ||
      isDeletingBlockedEmail ||
      isDeletingBlockedPhoneNumber,
    hasMore: hasMoreBlockedEmails || hasMoreBlockedPhoneNumbers,
    loadMore,
  };
};
